import {
  GraphType,
  GraphKind,
  GraphStats,
  GraphScalingType,
  DaysOfOperation,
  GraphResponse,
  GraphScalingOption,
  GraphOverlayType,
  OrientationGraph,
} from '@business/entities/graph';
import { Moment } from 'moment';
import moment from 'moment';

export function getAllGraphTypes() {
  return [GraphType.daily, GraphType.weekly, GraphType.monthly, GraphType.quarterly, GraphType.yearly];
}

export function getGraphTypeLabel(type: GraphType) {
  switch (type) {
    case GraphType.daily:
      return 'Daily';
    case GraphType.weekly:
      return 'Weekly';
    case GraphType.monthly:
      return 'Monthly';
    case GraphType.quarterly:
      return 'Quarterly';
    case GraphType.yearly:
      return 'Yearly';
  }
}

export function getGraphTypeItems() {
  return getAllGraphTypes().map(type => ({
    value: type,
    label: getGraphTypeLabel(type),
  }));
}

export function getGraphKinds() {
  return [GraphKind.regular, GraphKind.equation, GraphKind.combination];
}

export function getGraphKindLabel(kind: GraphKind) {
  switch (kind) {
    case GraphKind.regular:
      return 'Regular Graph';
    case GraphKind.equation:
      return 'Equation Graph';
    case GraphKind.combination:
      return 'Combination Overlay';
  }
}

export function getGraphKindItems() {
  return getGraphKinds().map(kind => ({
    value: kind,
    label: getGraphKindLabel(kind),
  }));
}

export function getAllGraphStats() {
  return [GraphStats.currency, GraphStats.percentage, GraphStats.numeric];
}

export function getGraphStatSymbol(stat: GraphStats, showNumeric = false) {
  switch (stat) {
    case GraphStats.currency:
      return '$';
    case GraphStats.percentage:
      return '%';
    case GraphStats.numeric:
      return showNumeric ? '1,2,3' : '';
  }
}

export function getGraphStatLabel(stat: GraphStats) {
  switch (stat) {
    case GraphStats.currency:
      return `(${getGraphStatSymbol(GraphStats.currency)}) Currency`;
    case GraphStats.percentage:
      return `(${getGraphStatSymbol(GraphStats.percentage)}) Percentage`;
    case GraphStats.numeric:
      return `(${getGraphStatSymbol(GraphStats.numeric, true)}) Numeric`;
  }
}

export function getGraphStatItems() {
  return getAllGraphStats().map(stat => ({
    value: stat,
    label: getGraphStatLabel(stat),
  }));
}

export function getGraphScalingItems() {
  return [
    { label: 'Auto', value: GraphScalingType.auto },
    {
      label: 'Custom',
      value: GraphScalingType.custom,
    },
  ];
}

export function getScalingOptionItems() {
  return [
    {
      label: 'Maintain Graph Scaling',
      value: GraphScalingOption.maintain,
    },
    { label: 'Match Original Graph Scaling', value: GraphScalingOption.match },
  ];
}

export function isDayBlocked(d: Moment, days: DaysOfOperation) {
  switch (d.day()) {
    case 0:
      return !days.sun;
    case 1:
      return !days.mon;
    case 2:
      return !days.tue;
    case 3:
      return !days.wed;
    case 4:
      return !days.thu;
    case 5:
      return !days.fri;
    case 6:
      return !days.sat;
  }
}

export function isDateBlocked(d: Moment, companyWeekEnding: number, graph: GraphResponse) {
  if (graph.type === GraphType.daily) {
    return isDayBlocked(d, graph.days);
  }

  if (graph.type === GraphType.weekly) {
    const weekEnding = companyWeekEnding === 7 ? 0 : companyWeekEnding;
    return d.day() !== weekEnding;
  }

  if (graph.type === GraphType.monthly) {
    return !d.isSame(d.clone().endOf('month'), 'day');
  }

  if (graph.type === GraphType.quarterly) {
    const isEndOfQuarter = [2, 5, 8, 11].includes(d.month());
    const isEndOfMonth = d.isSame(d.clone().endOf('month'), 'day');
    const final = isEndOfQuarter && isEndOfMonth;
    return !final;
  }

  if (graph.type === GraphType.yearly) {
    return !d.isSame(d.clone().endOf('year'), 'day');
  }

  return false;
}

export function getDefaultGraphConfig() {
  return {
    dataLabels: {
      enabled: false,
    },
    chart: {
      toolbar: { show: false },
    },
    stroke: {
      curve: 'straight',
    },

    markers: {
      size: 5,
      colors: ['#DE524C'],
    },
    grid: {
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
      show: true,
    },
  } as ApexCharts.ApexOptions;
}

// this method should return the types allowed based on the type selected if the type is daily the allowed types are daily, weekly, monthly, quarterly, yearly
export function getAllowedGraphTypes(type: GraphType) {
  switch (type) {
    case GraphType.daily:
      return [GraphType.daily];
    case GraphType.weekly:
      return [GraphType.daily, GraphType.weekly];
    case GraphType.monthly:
      return [GraphType.daily, GraphType.weekly, GraphType.monthly];
    case GraphType.quarterly:
      return [GraphType.daily, GraphType.weekly, GraphType.monthly, GraphType.quarterly];
    case GraphType.yearly:
      return [GraphType.daily, GraphType.weekly, GraphType.monthly, GraphType.quarterly, GraphType.yearly];
  }
  //   switch (type) {
  //     case GraphType.daily:
  //       return [GraphType.daily, GraphType.weekly, GraphType.monthly, GraphType.quarterly, GraphType.yearly];
  //     case GraphType.weekly:
  //       return [GraphType.weekly, GraphType.monthly, GraphType.quarterly, GraphType.yearly];
  //     case GraphType.monthly:
  //       return [GraphType.monthly, GraphType.quarterly, GraphType.yearly];
  //     case GraphType.quarterly:
  //       return [GraphType.quarterly, GraphType.yearly];
  //     case GraphType.yearly:
  //       return [GraphType.yearly];
  //   }
}

export function getAllGraphOverlayTypes() {
  return [GraphOverlayType.overlayOffset, GraphOverlayType.overlayDateRange];
}

export function getGraphOverlayTypeLabel(type: GraphOverlayType) {
  switch (type) {
    case GraphOverlayType.overlayOffset:
      return 'Overlay by Offset';
    case GraphOverlayType.overlayDateRange:
      return 'Overlay by Date Range';
  }
}

export function getGraphOverlayTypeItems() {
  return getAllGraphOverlayTypes().map(type => ({
    value: type,
    label: getGraphOverlayTypeLabel(type),
  }));
}

export function getAllPerPageOptions() {
  return [1, 4];
}

export function getPerPageLabel(perPage: number) {
  switch (perPage) {
    case 1:
      return '1';
    case 4:
      return '4';
  }
}

export function getGraphsPerPageItems() {
  return getAllPerPageOptions().map(perPage => ({
    label: getPerPageLabel(perPage),
    value: perPage,
  }));
}

export function getAllGraphOrientations() {
  return [OrientationGraph.portrait, OrientationGraph.landscape];
}

export function getGraphOrientationLabel(orientation: OrientationGraph) {
  switch (orientation) {
    case OrientationGraph.portrait:
      return 'Portrait';
    case OrientationGraph.landscape:
      return 'Landscape';
  }
}

export function getGraphOrientationItems() {
  return getAllGraphOrientations().map(orientation => ({
    label: getGraphOrientationLabel(orientation),
    value: orientation,
  }));
}
